/**
 * Pure variant-assignment logic for homepage experiments — no DOM, no Sentry,
 * no config. The engine (`experiments.ts`) wires these into the `gp_vid` cookie
 * and Sentry; keeping the math here (as `dwell-clock.ts` does) lets it be unit
 * tested without a browser. See docs/experimentation.md.
 */

// One arm of an experiment. `weight` is relative — a `[{weight:3},{weight:1}]`
// pair splits 75/25 — so weights need not sum to anything in particular.
export interface Variant {
  key: string;
  weight: number;
}

export interface Experiment {
  key: string;
  variants: Variant[];
}

// FNV-1a (32-bit). A small, stable, non-cryptographic hash: we only need a
// well-distributed number from a string, and determinism *across deploys* — so
// the same visitor keeps the same bucket — which a seeded, fixed-constant hash
// guarantees where a random coin flip would not. `Math.imul` keeps the multiply
// in 32-bit; `>>> 0` reads the result as unsigned.
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

// Hash a string to a number in [0, 1). Dividing the 32-bit hash by 2^32 keeps
// the result strictly below 1, so it indexes cleanly into cumulative weights.
export function hashToUnit(input: string): number {
  return fnv1a(input) / 0x100000000;
}

// Map a unit value in [0, 1) onto a variant by cumulative weight. With weights
// [3, 1] the first variant owns [0, 0.75) and the second [0.75, 1). The final
// variant is the floating-point safety net for a `unit` that rounds onto the
// upper edge.
export function pickVariant(unit: number, variants: Variant[]): string {
  // An experiment with no variants is a programmer error — the final `return`
  // would otherwise read `variants[-1]` → undefined (typed as string) and hand
  // back a bogus assignment. Fail loud; the engine also skips such experiments.
  if (variants.length === 0) throw new Error('pickVariant: experiment has no variants');
  const total = variants.reduce((sum, v) => sum + v.weight, 0);
  const target = unit * total;
  let cumulative = 0;
  for (const variant of variants) {
    cumulative += variant.weight;
    if (target < cumulative) return variant.key;
  }
  return variants[variants.length - 1].key;
}

/**
 * The variant a visitor is assigned for an experiment. Deterministic in
 * `(vid, experiment.key)`: the same visitor always lands in the same bucket, and
 * the experiment key is mixed in so an unrelated experiment re-uses the same
 * `vid` without correlating assignments.
 */
export function assignVariant(vid: string, experiment: Experiment): string {
  return pickVariant(hashToUnit(`${vid}:${experiment.key}`), experiment.variants);
}
